import React from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@progress/kendo-react-buttons';
import cloneDeep from 'lodash.clonedeep';

import PopupMenu from '../popupMenu';
import { GetResourceValue, UpdateGameDocState } from '../../utils/game-document';
import { GameDocumentContext } from '../../contexts/game-document';
import { MenuList } from '../../features/game-document/maps/map-images-window';
import { MapTimerWindow } from '../../features/game-document/maps/map-timer-window';

const timerMenu: MenuList[] = [
  { classIcon: 'edit', textMenu: 'Manage', textClass: '' },
  { classIcon: 'change_circle', textMenu: 'Replace', textClass: '' },
  { classIcon: 'delete', textMenu: 'Remove', textClass: 'text-danger' }
];

export const MapBehaviours: React.FC<{
  toggleActionsEventDialogVisible: () => void;
}> = ({ toggleActionsEventDialogVisible }) => {
  const { zoneId } = useParams();
  const [state, setState] = React.useContext(GameDocumentContext);

  const [actionEditTimerDialogVisible, setActionEditTimerDialogVisible] =
    React.useState<boolean>(false);
  const [selectedMenu, setSelectedMenu] = React.useState<'Manage' | 'Replace'>(
    'Manage'
  );

  const timer = React.useMemo(() => {
    const { gameDocument } = state;

    let id = '';

    if (zoneId) {
      const zoneIndex = gameDocument?.rules?.worldMap?.zones?.findIndex(
        (x) => x?.zoneAssId === zoneId
      );
      if (zoneIndex !== undefined && zoneIndex !== -1) {
        id = gameDocument?.rules.worldMap.zones[zoneIndex].timerAssId ?? '';
      }
    } else {
      id = gameDocument?.rules.worldMap?.timerAssId ?? '';
    }

    return (gameDocument?.assets.timers ?? []).find((t) => t.id === id);
  }, [zoneId, state.gameDocument]);

  const onChangeEditTimerHandler = (
    value: string,
    gameDocument: (typeof state)['gameDocument']
  ) => {
    if (!gameDocument) return;

    const docCopy = cloneDeep(gameDocument);
    if (zoneId) {
      const zoneIndex = docCopy.rules?.worldMap?.zones?.findIndex(
        (x) => x?.zoneAssId === zoneId
      );
      if (zoneIndex !== undefined && zoneIndex !== -1) {
        docCopy.rules.worldMap.zones[zoneIndex].timerAssId = value;
      }
    } else {
      if (!docCopy.rules.worldMap) {
        docCopy.rules.worldMap = {} as any;
      }
      docCopy.rules.worldMap.timerAssId = value;
    }

    setState((prev) => UpdateGameDocState(prev, docCopy));
  };

  const handleMenuSelected = (menu: MenuList) => {
    if (menu.textMenu === 'Remove') {
      onChangeEditTimerHandler('', state.gameDocument);
    } else if (['Manage', 'Replace'].includes(menu.textMenu ?? '')) {
      setSelectedMenu(menu.textMenu as 'Manage');
      setActionEditTimerDialogVisible(true);
    }
  };

  return (
    <>
      <div className={'d-flex gap-2 align-items-center'}>
        <h5 className={'mt-2'}>Actions</h5>
        <span
          style={{
            fontSize: '12px'
          }}>
          Define how the map responds
        </span>
      </div>

      <Button
        themeColor={'dark'}
        onClick={toggleActionsEventDialogVisible}
        style={{
          width: '100%',
          backgroundColor: '#003A64'
        }}>
        <span
          className={'material-symbols-outlined'}
          style={{
            fontSize: '16px'
          }}>
          alt_route
        </span>
        <span className={'ml-2'}>Edit events and actions</span>
      </Button>

      <div className={'d-flex flex-column w-100 mt-2'}>
        <div className={'d-flex gap-2 align-items-center'}>
          <h5 className={'mt-2'}>Map Timer</h5>
          <span
            title={'Add and manage this map’s timer.'}
            style={{
              fontSize: '12px'
            }}>
            Add and manage this map’s timer.
          </span>
        </div>

        {!timer && (
          <Button
            themeColor={'dark'}
            onClick={() => setActionEditTimerDialogVisible(true)}
            style={{
              width: '100%',
              backgroundColor: '#003A64'
            }}>
            <span
              className={'material-symbols-outlined'}
              style={{
                fontSize: '16px'
              }}>
              alarm
            </span>
            <span className={'ml-2'}>Add timer</span>
          </Button>
        )}

        {timer && (
          <div
            key={timer.id}
            className={
              'd-flex w-100 justify-content-between align-items-center gap-2'
            }>
            <div
              className={'card w-100 border-0'}
              style={{
                backgroundColor: '#763FC84A',
                color: '#003A64'
              }}>
              <div
                className={
                  'card-body p-2 d-flex w-100 justify-content-between align-items-center'
                }>
                <div className={'d-flex gap-2'}>
                  <span className={'material-symbols-outlined'}>avg_pace</span>
                  {(state.gameDocument &&
                    GetResourceValue(
                      state.gameDocument,
                      timer.titleResId ?? ''
                    )) ||
                    timer.name ||
                    'Untitled Timer'}
                </div>
                <div>
                  <PopupMenu
                    id={0}
                    idString={timer.id}
                    buttonColor={'black'}
                    menus={timerMenu}
                    onMenuSelected={(_, menu) => handleMenuSelected(menu)}
                    iconClass={'k-icon k-i-more-vertical h5 mb-0'}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {actionEditTimerDialogVisible && (
        <MapTimerWindow
          selectedMenu={selectedMenu}
          setActionEditTimerDialogVisible={setActionEditTimerDialogVisible}
          {...(timer && {
            initialTimerId: timer.id
          })}
        />
      )}
    </>
  );
};

